/*
 * "Modelos de lenguaje" (UX-11), from the mockup's `.models`, `.tiers`, `.tier-body`, `.two`,
 * `.input-affix`, `.action-bar`, `.cost` and `.timeline`: the form beside a 300px history column,
 * one column below 1280px; the tier cards side by side from 1024px.
 */
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: var(--space-6);
  align-items: start;
}

.main,
.aside {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
}

@media (max-width: 1279px) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-2);
  margin: 0;
  font-size: var(--font-size-12);
  color: var(--text-muted);
}

/* A middle dot between the parts of the meta line, drawn, not read. */
.summary > span + span::before {
  margin-inline-end: var(--space-2);
  content: "·";
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.notice {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  padding: 10px var(--space-3);
  border-radius: var(--radius-control);
  background: var(--info-surface);
  color: var(--text);
  font-size: var(--font-size-13);
}

.notice > svg {
  flex: none;
  margin-top: 2px;
  color: var(--info-text);
}

.notice p {
  margin: 0;
}

.tiers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 1023px) {
  .tiers {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cardTitle,
.historyTitle {
  margin: 0;
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-semibold);
}

.historyTitle {
  margin-block-end: var(--space-3);
}

.cardBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
}

.cardBody > p {
  margin: 0;
  font-size: var(--font-size-13);
}

.meta {
  margin: 0;
  font-size: var(--font-size-12);
  color: var(--text-muted);
  font-weight: var(--font-weight-regular);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.label {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
  color: var(--text);
}

.hint {
  margin: 0;
  font-size: var(--font-size-12);
  color: var(--text-subtle);
}

.error {
  margin: 0;
  font-size: var(--font-size-12);
  color: var(--danger-text);
}

.limitRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.limitInput {
  display: block;
  width: 140px;
  font-variant-numeric: tabular-nums;
}

.unit {
  font-size: var(--font-size-12);
  color: var(--text-muted);
}

.priceGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.legend {
  padding: 0;
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
}

.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

@media (max-width: 1023px) {
  .two {
    grid-template-columns: 1fr;
  }
}

/* The mockup's `.input-affix`: the currency inside the field, before the amount. */
.affix {
  position: relative;
  display: block;
}

.affixText {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 10px;
  display: flex;
  align-items: center;
  font-size: var(--font-size-12);
  color: var(--text-muted);
  pointer-events: none;
}

.affix input {
  padding-inline-start: 42px;
  font-variant-numeric: tabular-nums;
}

.refusal,
.conflict {
  margin: 0;
  padding: 10px var(--space-3);
  border-radius: var(--radius-control);
  background: var(--danger-surface);
  color: var(--text);
  font-size: var(--font-size-13);
}

.conflict {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: flex-start;
}

.refusal:focus-visible,
.conflict:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

/* The mockup's `.action-bar`: sticky at the bottom of the page, a floating layer with its shadow. */
.bar {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
  box-shadow: var(--shadow-popover);
}

.cost {
  margin: 0;
  font-size: var(--font-size-13);
  color: var(--text-muted);
}

.amount {
  font-size: var(--font-size-16);
  font-weight: var(--font-weight-semibold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.previewButton {
  display: inline-flex;
}

.spacer {
  flex: 1;
}

.saved {
  margin: 0;
  font-size: var(--font-size-13);
  color: var(--success-text);
}

.skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.historyLoading {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

/* The mockup's `.timeline`: a dot per revision on a hairline, the newest dot in the accent. */
.timeline {
  margin: 0;
  padding: 0;
  list-style: none;
}

.revision {
  position: relative;
  padding: 0 0 var(--space-4) 20px;
  font-size: var(--font-size-13);
}

.revision::before {
  position: absolute;
  top: 7px;
  left: 4px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border-strong);
  content: "";
}

.revision::after {
  position: absolute;
  top: 18px;
  bottom: 0;
  left: 7px;
  width: 1px;
  background: var(--border);
  content: "";
}

.revision:last-child::after {
  display: none;
}

.revision:first-child::before {
  background: var(--action-surface);
}

.revision p {
  margin: 0;
}

.fields {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin-top: var(--space-1) !important;
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* A refusal of the whole price list, at the top of the prices card. */
.cardError {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--font-size-12);
  color: var(--danger-text);
}

/* Mockup v3 P7: a tier's catalogue picker button with its help, and the source tag of a price. */
.catalogue {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.priceTag {
  margin-inline-start: 6px;
  color: var(--text-muted);
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-regular);
}

.priceTag[data-edited] {
  color: var(--text);
  font-style: italic;
}
